import { TAppDispatch, TGetState } from 'store/types'
import { getAiImageVersions_API } from 'api'
import { IAIImageProject } from 'store/types/aiImage'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import {
  getImageModelQuality,
  normalizeImageVersionsResponse
} from 'store/features/aiImage/utils'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import aiImageSlice from 'store/features/aiImage/slice'

const { actions } = aiImageSlice

const getVersionHistory =
  (projectId: string | number) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    // Non-numeric ids (e.g. temporary hash for in-flight regeneration) can't be
    // queried. Mark history as loaded-empty so the empty state renders instead
    // of a white blank.
    if (!projectId || isNaN(Number(projectId))) {
      dispatch(actions.setVersionHistoryItems([]))
      return
    }

    dispatch(actions.resetVersionHistory())
    dispatch(actions.setVersionHistoryLoading(true))

    try {
      const { data } = await getAiImageVersions_API(projectId)
      const projects = normalizeImageVersionsResponse(data)
      const modelsData = aiImageSelectors.modelsData(getState())
      const modelsByCode = new Map(modelsData.map(m => [m.code, m.name]))
      const items: IVersionHistoryItem[] = projects.map(
        (img: IAIImageProject) => ({
          id: img.id,
          url: img.images?.[0]?.filePath || '',
          prompt: img.prompt,
          type: 'image' as const,
          modelName: modelsByCode.get(img.model) ?? img.model,
          modelQuality: getImageModelQuality(img),
          width: img.width,
          height: img.height,
          assetId: img.images?.[0]?.id ?? null,
          aspectRatio: img.aspectRatio,
          process: img.status === 'processing'
        })
      )
      dispatch(actions.setVersionHistoryItems(items))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(actions.setVersionHistoryItems([]))
    } finally {
      dispatch(actions.setVersionHistoryLoading(false))
    }
  }

export const versionHistoryThunks = {
  getVersionHistory
}
